/**
 * User-facing error messages for channel adapters (Telegram, Slack, etc.).
 *
 * Single source of truth for error copy and support contact. All relay
 * error paths MUST use these functions instead of inline strings.
 */

import { STUDIO_MAX_APP_BYTES, STUDIO_MAX_FILE_BYTES } from "./studio-app";

const SUPPORT_EMAIL = "help@clawcraft.ca";

const SUPPORT_SUFFIX = `If this keeps happening, reach out to support at ${SUPPORT_EMAIL}`;

export function agentError({ status }: { status: number }): string {
  return `⚠️ Your agent encountered an error (${status}). Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function agentTimeout(): string {
  return `⏳ Your agent took too long to respond. Please try again — it may be processing a complex request. ${SUPPORT_SUFFIX}`;
}

export function agentUnreachable(): string {
  return `⚠️ Something went wrong reaching your agent. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function integrationAuthExpired({ provider }: { provider: string }): string {
  return `Your ${provider} connection has expired. Please reconnect from your dashboard. ${SUPPORT_SUFFIX}`;
}

export function integrationNotConnected({ provider }: { provider: string }): string {
  return `${provider} is not connected. Connect it from your dashboard. ${SUPPORT_SUFFIX}`;
}

export function integrationRateLimited(): string {
  return `Integration rate limit reached. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function integrationUnavailable(): string {
  return `Integration service is temporarily unavailable. Please try again shortly. ${SUPPORT_SUFFIX}`;
}

// ─── Validation pipeline error copy ──────────────────────────────────
// Returned by the Convex validation action (Spec 2b) when the semantic
// stage can't run. Strings only — no control surfaces, no callers in
// 2a; consumed by the action + future UI banner.

export function validationPodUnavailable(): string {
  return `The validation service is temporarily unavailable. Your workflow wasn't checked; try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function validationLlmTimeout(): string {
  return `The semantic validator took too long to respond and was skipped. Your spec's structural checks ran successfully. ${SUPPORT_SUFFIX}`;
}

export function validationTreasuryExhausted(): string {
  return `Your validation credit is exhausted. Structural checks still ran, but the semantic check was skipped. Top up your credit to re-enable it.`;
}

export function validationSpecFileMissing(): string {
  return `Your workflow file hasn't reached your agent's workspace yet. Ask your agent to save the workflow, then check it again. ${SUPPORT_SUFFIX}`;
}

export function validationCheckFailed({ reason }: { reason: string }): string {
  return `The check couldn't run (${reason}). Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function validationUnconfirmed(): string {
  return `The check may not have landed — try again.`;
}

// ─── Execution runtime error copy ────────────────────────────────────
// Returned by the Convex execution action (Spec 3b) when requestStart
// can't proceed. Strings only — UI maps action-return outcomes to these.

export function executionPodUnavailable(): string {
  return `The execution service is temporarily unavailable. Your run wasn't started; try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function executionAlreadyInFlight(): string {
  return `An execution is already running for this spec. Wait for it to finish before starting another.`;
}

export function executionSpecInactive(): string {
  return `This spec is inactive. Activate it before running. ${SUPPORT_SUFFIX}`;
}

// ─── Billing error copy ──────────────────────────────────────────────
// Surfaced by useBilling when the Stripe checkout/portal actions throw.

export function checkoutUnavailable(): string {
  return `Checkout is temporarily unavailable. You haven't been charged — please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function billingPortalUnavailable(): string {
  return `The billing portal is temporarily unavailable. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

// ─── Notebook error copy ─────────────────────────────────────────────
// Surfaced by useNotebook when the notebookActions list/read/write/delete
// actions throw. The action layer maps GCS/validation failures to coded
// errors (INVALID_NOTE_PATH, NOTE_TOO_LARGE, NOT_FOUND) or 502-class
// transport throws; the hook picks the matching copy. No auto-retry — the
// user retries via the explicit refresh/save affordance.

// NO "Please refresh to try again" here, unlike its `studioAppsLoadFailed`
// sibling: the notebook's list-failure pane renders an explicit "Try again"
// button directly beneath this line, so the instruction contradicted the
// affordance sitting next to it. Keeping the sentence short also keeps the
// pane's bold lead line to one measure instead of a viewport-wide run-on.
// Studio's copy KEEPS the sentence — its render path has no retry button.
export function notebookLoadFailed(): string {
  return `We couldn't load your notes. ${SUPPORT_SUFFIX}`;
}

export function notebookReadFailed(): string {
  return `We couldn't open that note. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function notebookSaveFailed(): string {
  return `We couldn't save your note. Please try again — nothing you typed was lost. ${SUPPORT_SUFFIX}`;
}

export function notebookDeleteFailed(): string {
  return `We couldn't delete that note. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function notebookInvalidPath(): string {
  return `That file name isn't allowed. Use a plain name ending in .md, with no path segments that lead outside the notebook.`;
}

export function notebookNoteTooLarge(): string {
  return `That note is too large to save (the limit is 1 MiB). Trim it down and try again.`;
}

export function notebookNoteNotFound(): string {
  return `That note no longer exists — it may have been deleted or renamed.`;
}

export function notebookInvalidFolderName(): string {
  return `That folder name isn't allowed. Use a plain name with no slashes or special characters.`;
}

/** Folder rename/delete/move loops stop on the FIRST failure (write-before-
 *  delete: a note may briefly exist at both paths, never at neither). The
 *  failed path is named so the user can retry precisely. */
export function notebookFolderOpFailed({ failedPath }: { failedPath: string }): string {
  return `We couldn't finish moving your notes — "${failedPath}" failed and the rest were left in place. Nothing was lost; the list has been refreshed. Please try again. ${SUPPORT_SUFFIX}`;
}

// ─── Notebook share (publish-to-web) error copy ──────────────────────
// Surfaced INLINE in the ShareMenu popover by useNotebookShares when the
// publish action / unpublish mutation throw coded errors (SHARE_TOO_LARGE,
// NOT_FOUND, INVALID_NOTE_PATH) — never as a page banner.

export function notebookShareTooLarge(): string {
  return `This note is too large to publish (the limit is 700 KB — embedded images count). Trim it down and try again.`;
}

export function notebookShareNoteMissing(): string {
  return `This note hasn't reached your notebook yet — save it first, then publish.`;
}

export function notebookPublishFailed(): string {
  return `We couldn't publish this note. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function notebookUnpublishFailed(): string {
  return `We couldn't unpublish this note. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

// ─── Studio error copy ───────────────────────────────────────────────
// Surfaced by useStudioApps / useStudioShares when the studio actions and
// share mutations throw coded errors (APP_TOO_LARGE, APP_FILE_TOO_LARGE,
// APP_LIMIT_EXCEEDED, NOT_FOUND, SHARE_NAME_EXHAUSTED) or transport
// failures. Support-suffix rule: benign caps / gone-states carry no
// suffix; genuine failures do.

export function studioAppTooLarge(): string {
  const appMib = STUDIO_MAX_APP_BYTES / (1024 * 1024);
  const fileMib = STUDIO_MAX_FILE_BYTES / (1024 * 1024);
  return `This app is too large to load (the limit is ${appMib} MiB across all files, ${fileMib} MiB per file). Ask your agent to slim it down.`;
}

export function studioTooManyFiles(): string {
  return `This app has too many files (the limit is 32). Ask your agent to consolidate it.`;
}

export function studioTooManyApps(): string {
  return `You've reached the limit of 50 apps. Delete an app you no longer need, then try again.`;
}

export function studioAppNotFound(): string {
  return `That app no longer exists — it may have been deleted or renamed.`;
}

export function studioShareFailed(): string {
  return `We couldn't publish this app. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function studioUnshareFailed(): string {
  return `We couldn't unpublish this app. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

// Generic transport-failure copy for the owner surfaces (list / read /
// delete) — surfaced by useStudioApps when an action throws without a
// recognized code. Shape matches the notebook precedent
// (`notebookLoadFailed`/`notebookDeleteFailed`).

export function studioAppsLoadFailed(): string {
  return `We couldn't load your apps. Please refresh to try again. ${SUPPORT_SUFFIX}`;
}

export function studioAppReadFailed(): string {
  return `We couldn't open that app. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function studioAppDeleteFailed(): string {
  return `We couldn't delete that app. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

// ─── Task intent-relay error copy ────────────────────────────────────
// Surfaced inline per affordance (check / capture input / decision
// form) when an intent_relays row lands `failed` or goes stale — never
// as a page banner.

export function taskRelayPodUnavailable(): string {
  return `Your agent is offline, so this couldn't be sent. Try again once it's back up. ${SUPPORT_SUFFIX}`;
}

export function taskRelayFailed(): string {
  return `This couldn't be sent to your agent. Please try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function taskCaptureUnavailable(): string {
  return `Your capture couldn't be sent to your agent. Your draft is kept — try again in a moment. ${SUPPORT_SUFFIX}`;
}

export function taskRelayUnconfirmed(): string {
  return `This may not have landed — check in chat with your agent.`;
}

// ─── Linq SMS channel error copy ─────────────────────────────────────
// Surfaced when a Linq message.failed event arrives or outbound delivery
// otherwise can't complete. `reason` is the Linq-reported failure string.

export function linqDeliveryFailed({ reason }: { reason: string }): string {
  return `⚠️ Your SMS reply couldn't be delivered: ${reason}. ${SUPPORT_SUFFIX}`;
}

export { SUPPORT_EMAIL };
